import type { FulfillmentOrderLine, FulfillmentResourceMentionCandidate } from "../types";
import type { EntryExistingDraft, EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { parseStayEntry } from "./confirmationStayEntryService";
import { prepareExistingStayEntry, parseExistingStayEntry } from "./confirmationExistingStayEntry";
import { validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";
import { parseConfirmationDayExpression } from "./confirmationEntryParser";
import { reconcileConfirmationEntryIdentities } from "./confirmationEntryIdentity";

/** 名称至少包含文字或数字；过滤历史占位标点，不过滤正常中英文或数字酒店。 */
export function isStayResourceName(name: string | null | undefined): boolean {
  return Boolean(name && /[\p{L}\p{N}]/u.test(name));
}

/** 住宿服务日按同住宿点的连续记录合并；不把空日期或间隔两段拼成全程。 */
export function buildStayRanges(days: ReadonlyArray<{ dayNo: number; date: string | null; stayName?: string | null }>) {
  const ranges: Array<{ name: string; range: string; label: string; nights: number; days: number[] }> = [];
  for (const day of [...days].sort((a, b) => a.dayNo - b.dayNo)) {
    const name = day.stayName?.trim();
    if (!isStayResourceName(name)) continue;
    const previous = ranges.at(-1);
    if (previous && previous.name === name && previous.days.at(-1)! + 1 === day.dayNo) previous.days.push(day.dayNo);
    else ranges.push({ name: name!, range: "", label: "", nights: 0, days: [day.dayNo] });
  }
  return ranges.map(item => {
    const range = item.days.length === 1 ? String(item.days[0]) : `${item.days[0]}-${item.days.at(-1)}`;
    return { ...item, range, label: `${item.name}·${range}`, nights: item.days.length };
  });
}

/** 比较实际日集合，允许历史 D1-D2 与新范围 1-2 指向同一组。 */
export function sameStayRange(left: string, right: string): boolean {
  const a = parseConfirmationDayExpression(left)?.numbers, b = parseConfirmationDayExpression(right)?.numbers;
  return a && b ? JSON.stringify(a) === JSON.stringify(b) : left.trim() === right.trim();
}

/** 只汇总真实候选的容量；未知房型不凭名称猜床位。 */
export function stayCapacity(lines: Partial<FulfillmentOrderLine>[], candidates: readonly FulfillmentResourceMentionCandidate[]) {
  let rooms = 0, beds = 0, unknown = false;
  for (const line of lines) {
    const quantity = Number(line.quantity || 0);
    rooms += quantity;
    const room = candidates.find(item => item.skuName === (line.itemSpecSnapshot || line.skuNameSnapshot));
    if (room?.roomCapacity && room.roomCapacity > 0) beds += quantity * room.roomCapacity;
    else if (quantity > 0) unknown = true;
  }
  return { rooms, beds, unknown };
}

/** 酒店全文按原文与唯一房型身份回填；新增房型不会覆盖已存在的原行。 */
export function parseStayHotelDraft(input: EntryParseBaseInput, originals: FulfillmentOrderLine[]): EntryParseResult & { drafts: EntryExistingDraft[] } {
  const seeds = originals.map(row => prepareExistingStayEntry(row, input.routeDays));
  // 未修改的历史房型可以缺少旧资源引用；浏览其他酒店不能强迫重建历史费用。
  const hotelId = (provider: EntryParseBaseInput["provider"]) => provider && "spuId" in provider ? provider.spuId : null;
  if (seeds.length && input.text === seeds.map(seed => seed.input.text).join("\n") && seeds.every(seed => sameStayRange(seed.input.range, input.range) && hotelId(seed.input.provider) === hotelId(input.provider))) {
    return { items: [], errors: [], drafts: originals.map(base => ({ base, patch: {}, removed: false })) };
  }
  const parsed = parseStayEntry({ ...input, allowZeroQuantity: originals.some(row => !row.id.startsWith("local-")) });
  const normalized = (text: string) => text.normalize("NFKC").replace(/\s+/g, "").replace(/[xX*]/g, "×");
  const originalText = seeds.map(seed => seed.input.text).join("\n");
  let offset = 0;
  const identities = seeds.map((seed, index) => {
    const sourceStart = offset;
    offset += seed.input.text.length + 1;
    return { id: String(index + 1), raw: normalized(seed.input.text), identity: seed.baseline?.itemSpecSnapshot || seed.base.skuNameSnapshot || seed.base.itemSpecSnapshot || "", text: originalText, sourceStart, sourceEnd: offset - 1 };
  });
  // 相同原文不携带交易主键；删改其中一笔不能推断应改哪家结算方。
  const duplicateChanged = identities.some(record => {
    const same = identities.filter(other => other.raw === record.raw);
    return same.length > 1 && parsed.items.some(item => item.patch.itemSpecSnapshot === record.identity)
      && parsed.items.filter(item => normalized(item.raw) === record.raw).length !== same.length;
  });
  const reconciled = reconcileConfirmationEntryIdentities(parsed.items.map(item => ({ ...item, raw: normalized(item.raw) })), identities,
    item => item.patch.itemSpecSnapshot || "", input.text, false, () => true);
  if (duplicateChanged || reconciled.ambiguous) {
    return { items: [], errors: [...parsed.errors, { raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "resource", message: "重复房型费用无法唯一对应原行，草稿已保留；请只勾选目标原行后重新打开修改" }], drafts: originals.map(base => ({ base, patch: {}, removed: false })) };
  }
  const matches = new Map<number, number>();
  const used = new Set<number>();
  reconciled.identities.forEach((record, index) => {
    const seedIndex = Number(record.id) - 1;
    if (seedIndex < seeds.length) { matches.set(index, seedIndex); used.add(seedIndex); }
  });
  const remainingSeeds = seeds.map((_, i) => i).filter(i => !used.has(i));
  const remainingItems = parsed.items.map((_, i) => i).filter(i => !matches.has(i));
  if (remainingSeeds.length === 1 && remainingItems.length === 1) {
    matches.set(remainingItems[0], remainingSeeds[0]); used.add(remainingSeeds[0]);
  } else if (remainingSeeds.length && remainingItems.length && !parsed.errors.length) {
    parsed.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "resource", message: "多条原房型同时变化，无法确定对应关系；请先修改一条房型并应用，再调整其他房型，避免覆盖原费用" });
  }
  const drafts: EntryExistingDraft[] = originals.map(base => ({ base, patch: {}, removed: false }));
  const additions: EntryParseResult["items"] = [];
  for (const [index, item] of parsed.items.entries()) {
    const seedIndex = matches.get(index);
    if (seedIndex === undefined) {
      const errors = validateFulfillmentLineDraft({ ...item.patch, id: "local-stay-preview" } as FulfillmentOrderLine);
      if (errors.length) parsed.errors.push({ ...item, field: "formula", message: errors.join("；") });
      else additions.push(item);
    } else {
      const result = parseExistingStayEntry(seeds[seedIndex], { ...input, text: item.raw, settlement: input.settlement === undefined ? seeds[seedIndex].input.settlement : input.settlement });
      drafts[seedIndex].patch = result.patch;
      parsed.errors.push(...result.errors.map(error => ({ ...error, sourceStart: item.sourceStart, sourceEnd: item.sourceEnd })));
    }
  }
  if (!parsed.errors.length) drafts.forEach((draft, i) => { draft.removed = !used.has(i); });
  return { items: additions, errors: parsed.errors, drafts };
}
